import { defineStore } from 'pinia';
import i18n from '@/plugins/vue-i18n';

import { useBoqStore } from '@/stores/boqs.store';
import { BOQItem } from '@/types/api/sites.types';
import { createClaims } from '@/api/claims.service';
import { useCartsStore } from '@/stores/carts.store';
import { useOrdersStore } from '@/stores/orders.store';
import { useToastStore } from '@/stores/utils/toast.store';
import {
  CreateOrdersBody,
  OrderClaim,
  Retention,
  ValueContraChargeData,
} from '@/types/api/orders.types';
import { analyticsService } from '@/plugins/post-hog';
import { selectedOrgId } from '@/common/utils/role.utils';

const { t } = i18n.global;

export const useClaimsStore = defineStore('claims', {
  state: () => ({
    retentions: [] as Retention[],
    newRetentionCount: 1,
    itemContraCharges: [] as BOQItem[],
    valueContraCharges: [] as ValueContraChargeData[],
    newValueContraChargeCount: 1,
  }),

  actions: {
    async createClaims(data: CreateOrdersBody, siteId?: string): Promise<OrderClaim[]> {
      const boqStore = useBoqStore();
      const cartStore = useCartsStore();
      const toastStore = useToastStore();
      const orderStore = useOrdersStore();
      const response = await createClaims(data);

      if (response.status === 200) {
        const orders = response.data.data;
        toastStore.showToast('success', t('claims.createClaim.toast.title'));

        for (const order of orders) {
          analyticsService.capture('boq_claim_created', {
            org_id: selectedOrgId.value!,
            order_id: order.id,
          });
        }

        for (const order of orders) {
          const retentionData = this.retentions
            .filter((retention) => retention.vendorId === order.vendorId)
            .map((retention) => ({ percent: retention.percent }));

          if (retentionData.length > 0) {
            await orderStore.addRetention(order.id, retentionData, order.version);
          }
        }

        for (const order of orders) {
          const itemContraChargeData = this.itemContraCharges
            .filter((item) => item.newVendorId === order.vendorId)
            .map((item) => ({
              itemPath: item.path,
              itemInGroup: !!item.groupName,
              rate: item.defaultRate ?? 0,
              quantity: item.defaultQuantity ?? 1,
            }));

          if (itemContraChargeData.length > 0) {
            await orderStore.addItemContraCharge(order.id, itemContraChargeData, order.version);
          }
        }

        for (const order of orders) {
          const valueContraChargeData = this.valueContraCharges
            .filter((item) => item.vendorId === order.vendorId)
            .map((item) => ({ amount: item.amount }));

          if (valueContraChargeData.length > 0) {
            await orderStore.addValueContraCharge(order.id, valueContraChargeData, order.version);
          }
        }

        if (siteId && boqStore.hasBoqForSite(siteId)) {
          await boqStore.getBOQ(siteId, true);
        }

        cartStore.clearCartStore();
        this.retentions = [];
        this.newRetentionCount = 1;
        this.itemContraCharges = [];
        this.valueContraCharges = [];
        this.newValueContraChargeCount = 1;

        return orders;
      }

      return [];
    },
  },
});
